import React from "react";
import { GitCompare, X } from "lucide-react";


/** Side-by-side comparison of live vs backtest stats + a drift banner. */
export function BacktestVsLivePanel({ data, onClose }) {
  const { live, backtest, drift } = data;
  const winDiffColor = drift.win_rate_diff >= 0 ? "text-[var(--profit)]" : "text-[var(--loss)]";
  const usdDiffColor = drift.net_usd_diff >= 0 ? "text-[var(--profit)]" : "text-[var(--loss)]";
  const fmtSigned = (n, prefix = "") => (n >= 0 ? `+${prefix}${n}` : `${prefix}${n}`);
  return (
    <div className="card-sharp p-5 mb-6" data-testid="bt-vs-live-panel">
      <div className="flex items-center gap-3 mb-4">
        <GitCompare className="text-[var(--accent)]" size={18}/>
        <div className="flex-1">
          <h2 className="font-display text-lg text-white">Backtest vs. Live</h2>
          <p className="text-[11px] font-mono text-[var(--text-muted)]">
            Live trades this deployment has executed compared against a fresh backtest of the same config.
            Any drift is market behavior — not a config difference.
          </p>
        </div>
        <button data-testid="bt-vs-live-close" onClick={onClose}
          className="p-1.5 border border-white/10 rounded hover:border-[var(--loss)] hover:text-[var(--loss)]">
          <X size={13}/>
        </button>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4 text-center">
        <DiffStat label="Trades diff" value={fmtSigned(drift.trades_diff)} testId="drift-trades"/>
        <DiffStat label="Win-rate diff" value={`${fmtSigned(drift.win_rate_diff)}%`} className={winDiffColor} testId="drift-winrate"/>
        <DiffStat label="Net P&L diff" value={fmtSigned(drift.net_usd_diff, "$")} className={usdDiffColor} testId="drift-usd"/>
      </div>
      <ComparisonTable live={live} backtest={backtest}/>
    </div>
  );
}


function DiffStat({ label, value, className = "text-white", testId }) {
  return (
    <div className="border border-white/5 rounded p-3" data-testid={testId}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">{label}</div>
      <div className={`font-display text-2xl mt-1 ${className}`}>{value}</div>
    </div>
  );
}


function ComparisonTable({ live, backtest }) {
  return (
    <div className="overflow-auto">
      <table className="w-full text-xs font-mono">
        <thead className="text-[var(--text-muted)] uppercase tracking-widest">
          <tr>
            <th className="text-left py-1"></th>
            <th className="text-right">Trades</th>
            <th className="text-right">Wins</th>
            <th className="text-right">Losses</th>
            <th className="text-right">Win %</th>
            <th className="text-right">Net pips</th>
            <th className="text-right pr-2">Net USD</th>
          </tr>
        </thead>
        <tbody>
          <StatRow chip="◆ LIVE" data={live} testId="btvs-live-row"/>
          <StatRow chip="BACKTEST" data={backtest} testId="btvs-bt-row"/>
        </tbody>
      </table>
    </div>
  );
}


function StatRow({ chip, data, testId }) {
  const net = data.net_usd ?? 0;
  return (
    <tr className="border-t border-white/5" data-testid={testId}>
      <td className="py-2"><span className="chip">{chip}</span></td>
      <td className="text-right text-white">{data.trades}</td>
      <td className="text-right text-[var(--profit)]">{data.wins}</td>
      <td className="text-right text-[var(--loss)]">{data.losses}</td>
      <td className="text-right text-white">{data.win_rate}%</td>
      <td className="text-right text-white">{data.net_pips}</td>
      <td className={`text-right pr-2 ${net >= 0 ? "text-[var(--profit)]" : "text-[var(--loss)]"}`}>
        ${net.toFixed(2)}
      </td>
    </tr>
  );
}
